import { DialogContentText, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { PublicKey } from '@solana/web3.js';
import { useMutation } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import strings from '../../../localization';
import { useConnection } from '../../../utils/connection';
import {
  USDT_BRIDGE_SERVICE_ADDRESS,
  USDT_BRIDGE_URL,
} from '../../../utils/env-variables';
import { queryClient } from '../../../utils/query-client';
import { useUsdtAccounts } from '../../../utils/tokens/hooks';
import { useWallet } from '../../../utils/wallet';
import DialogForm from '../../DialogForm';
import {
  findSuitableAccountsForTransfer,
  transferTokensBatched,
} from './UsdtSendDialog';

// Wrapping is asynchronous: /wrap enqueues and returns an id; the terminal
// result comes only from polling /wrap_status/:id.
const WRAP_STATUS_POLL_INTERVAL_MS = 3_000;
const WRAP_STATUS_POLL_TIMEOUT_MS = 15 * 60 * 1000;

// Final user-facing result of the wrap flow, rendered as a snackbar.
type WrapOutcome = {
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
};

// Wire shapes of POST /wrap and GET /wrap_status/:id.
type WrapStatusMint = {
  mint_address: string;
  status: string;
  wusdt_amount: string | null;
  burn_signature: string | null;
  mint_signature: string | null;
  reason: string | null;
};

type WrapStatusResponse = {
  status: string;
  wrap_id?: string;
  state?: string;
  reason?: string | null;
  mints?: WrapStatusMint[];
  message?: string;
};

// Legacy-only flow: the bridge burns surrendered legacy USDT and mints the
// same amount of wUSDT back to this wallet. wUSDT itself cannot be wrapped.
export default function UsdtWrapDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const wallet = useWallet();
  const connection = useConnection();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { data: tokenAccounts } = useUsdtAccounts('legacy');

  const [inputAmount, setInputAmount] = useState('');

  const availableAmount = (tokenAccounts ?? []).reduce((acc, curr) => {
    const tokenAmount = curr.account.data.parsed.info.tokenAmount;
    return acc + tokenAmount.uiAmount;
  }, 0);

  const wrapAmount = useMemo(
    () =>
      /^\d+\.?\d*$/.test(inputAmount)
        ? new Decimal(inputAmount)
        : new Decimal(0),
    [inputAmount],
  );

  const validAmount =
    wrapAmount.greaterThan(0) && wrapAmount.lessThanOrEqualTo(availableAmount);

  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      let statusId = enqueueSnackbar('Sending transaction...', {
        variant: 'info',
        persist: true,
      });
      const setStatus = (message: string) => {
        closeSnackbar(statusId);
        statusId = enqueueSnackbar(message, { variant: 'info', persist: true });
      };

      try {
        const suitableAccounts = findSuitableAccountsForTransfer(
          tokenAccounts ?? [],
          wrapAmount,
        );
        if (suitableAccounts.length === 0) {
          throw new Error(
            `No suitable accounts found for transfer amount: ${wrapAmount}`,
          );
        }

        // Tokens are surrendered to the bridge service wallet first; the
        // signed /wrap request then authorizes the burn-and-mint.
        const txSignatures = await transferTokensBatched(
          connection,
          wallet,
          suitableAccounts,
          new PublicKey(USDT_BRIDGE_SERVICE_ADDRESS!),
        );

        // Key order must match the bridge's signature message exactly.
        const requestBody = {
          domi_address: wallet.publicKey.toBase58(),
          mints: suitableAccounts.map((value) => {
            const accountInfo = value.account.account.data.parsed.info;
            return {
              mint_address: accountInfo.mint,
              withdraw_amount: value.transferAmount
                .mul(10 ** accountInfo.tokenAmount.decimals)
                .ceil()
                .toString(),
              decimals: accountInfo.tokenAmount.decimals,
            };
          }),
          tx_signatures: txSignatures,
        };

        const requestSignature = await wallet.createSignature(
          new TextEncoder().encode(JSON.stringify(requestBody)),
        );

        const response = await fetch(`${USDT_BRIDGE_URL}/wrap`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            ...requestBody,
            signature: requestSignature,
          }),
        });

        let json: WrapStatusResponse | null = null;
        try {
          json = await response.json();
        } catch {
          // Non-JSON body; rejected below via the response.ok / json checks.
        }
        console.debug('/wrap json', json);

        // "ok" means enqueued, not minted. Duplicate submissions return the
        // existing id, so a retry can never trigger a second mint.
        if (!response.ok || json?.status !== 'ok' || !json?.wrap_id) {
          throw new Error(json?.message ?? strings.requestFailed);
        }

        setStatus('Wrap submitted — waiting for the bridge to process it…');
        setInputAmount('');
        onClose();

        const outcome = await pollWrapStatus(json.wrap_id);
        closeSnackbar(statusId);
        enqueueSnackbar(outcome.message, {
          variant: outcome.variant,
          autoHideDuration: 10000,
        });
      } catch (err) {
        if (err instanceof Error) {
          closeSnackbar(statusId);
          enqueueSnackbar(err.message, { variant: 'error' });
          console.error(err);
        }
      } finally {
        // Burns, mints and refunds all move balances; refetch every token
        // account query (both programs) once the flow settles.
        queryClient.invalidateQueries({ queryKey: ['tokenAccounts'] });
      }
    },
  });

  const amountError = useMemo(() => {
    if (wrapAmount.lessThanOrEqualTo(0)) {
      return '';
    }
    if (wrapAmount.greaterThan(availableAmount)) {
      return 'Insufficient balance';
    }
    return '';
  }, [wrapAmount, availableAmount]);

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={() => mutate()}
      fullWidth
    >
      <DialogTitle>{strings.wrapUsdtTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{strings.wrapUsdtInfo}</DialogContentText>
        <TextField
          label={strings.amount}
          fullWidth
          variant="outlined"
          margin="normal"
          type="number"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  onClick={() => setInputAmount(availableAmount.toFixed(9))}
                >
                  {strings.max}
                </Button>
                <Typography color="primary">USDT</Typography>
              </InputAdornment>
            ),
            inputProps: {
              step: 10 ** -9,
            },
          }}
          value={inputAmount}
          onChange={(e) => setInputAmount(e.target.value.trim())}
          error={!!amountError}
          helperText={
            amountError || (
              <span onClick={() => setInputAmount(availableAmount.toFixed(9))}>
                {strings.max}: {availableAmount.toFixed(9)}
              </span>
            )
          }
        />
        {validAmount && (
          <DialogContentText>
            {strings.youWillReceive}: {wrapAmount.toFixed(9)} wUSDT
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div>
          <Button onClick={onClose}>{strings.cancel}</Button>
          <Button
            type="submit"
            color="primary"
            disabled={!validAmount || isLoading}
          >
            {strings.wrap}
          </Button>
        </div>
      </DialogActions>
    </DialogForm>
  );
}

// Poll /wrap_status until a terminal state or the deadline. Transient
// fetch/server errors are absorbed; the request is already durably queued.
async function pollWrapStatus(wrapId: string): Promise<WrapOutcome> {
  const deadline = Date.now() + WRAP_STATUS_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await sleep(WRAP_STATUS_POLL_INTERVAL_MS);

    let status: WrapStatusResponse | null = null;
    try {
      const response = await fetch(`${USDT_BRIDGE_URL}/wrap_status/${wrapId}`, {
        headers: { Accept: 'application/json' },
      });
      status = await response.json();
    } catch {
      continue;
    }

    if (status?.status !== 'ok') {
      continue;
    }

    const outcome = wrapOutcome(status);
    if (outcome) {
      return outcome;
    }
  }

  return {
    variant: 'info',
    message:
      'Wrap is still processing. Your wUSDT balance will update automatically once it completes.',
  };
}

// Map a bridge request state to a user-facing outcome. Returns null for
// non-terminal states (queued, processing) to continue polling.
function wrapOutcome(status: WrapStatusResponse): WrapOutcome | null {
  const reason = status.reason ? `: ${status.reason}` : '';

  switch (status.state) {
    case 'finished':
      return {
        variant: 'success',
        message: 'Wrap successful — your wUSDT has arrived',
      };
    case 'failed':
      return { variant: 'error', message: `Wrap failed${reason}` };
    case 'refunded':
      return {
        variant: 'error',
        message: `Wrap failed — the USDT was returned to your wallet${reason}`,
      };
    case 'partial': {
      const mints = status.mints ?? [];
      const finished = mints.filter((m) => m.status === 'finished').length;
      const detail = mints
        .filter((m) => m.status !== 'finished')
        .map(
          (m) =>
            `${shortenMint(m.mint_address)}: ${m.status}` +
            (m.reason ? ` — ${m.reason}` : ''),
        )
        .join('; ');
      return {
        variant: 'warning',
        message:
          `Wrap partially completed (${finished}/${mints.length} wrapped)` +
          (detail ? `. ${detail}` : ''),
      };
    }
    case 'needs_review':
      return {
        variant: 'info',
        message:
          'Wrap is processing under review. No action is needed — your funds are safe.',
      };
    default:
      return null;
  }
}

function shortenMint(address: string) {
  return address.length > 12
    ? `${address.slice(0, 4)}…${address.slice(-4)}`
    : address;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}
